import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import type { PaginationInterface } from 'shared/types/swr-pagination-interface'
import type { ManageSubscriptionsApiInterface } from 'modules/profile/manage-subscriptions/types/manage-subscriptions-interface'

const MANAGE_SUBSCRIPTIONS_API = '/api/settings/customer-subscriptions'

export const subscriptionKeys = {
  all: [MANAGE_SUBSCRIPTIONS_API] as const,
  list: (userId: number | null, url: string | null) =>
    [...subscriptionKeys.all, userId, url] as const,
}

interface UseManageSubscriptionsInterface<TData> extends Partial<
  UseQueryOptions<ManageSubscriptionsApiInterface, Error, TData>
> {
  limit?: number
  pagination: PaginationInterface
}

export function useManageSubscriptions<TData = ManageSubscriptionsApiInterface>({
  limit,
  pagination,
  ...options
}: UseManageSubscriptionsInterface<TData>): UseQueryResult<TData> {
  const { user } = useUser()
  const userId = isAuthorizedUser(user) ? user.id : null

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    ManageSubscriptionsApiInterface
  >({
    method: RequestMethodsEnum.get,
  })

  const url =
    pagination.startFromId === undefined
      ? `${MANAGE_SUBSCRIPTIONS_API}/list?pagination[limit]=${limit}`
      : `${MANAGE_SUBSCRIPTIONS_API}/list?pagination[startFrom]=${pagination.startFromId}&pagination[order]=${pagination.order}&pagination[limit]=${limit}`
  const enabled = Boolean(
    user && limit && (pagination.startFromId === undefined || pagination.startFromId),
  )

  return useQuery({
    queryKey: subscriptionKeys.list(userId, enabled ? url : null),
    queryFn: async () => fetcher(url),
    enabled,
    ...options,
  })
}
